import { call, put, takeEvery, all } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

import { getScene } from 'src/services/sceneClient';
import { deleteAsset, restoreAsset } from 'src/services/assetsClient';
import { logError } from 'src/loggingManager';
import { apiErrorMessage } from 'src/services/apiError';
import { SurfaceData } from 'src/types/services';
import {
    addScene,
    deleteSceneSuccess,
    deleteSceneFailed,
    deleteScene,
    restoreScene,
    restoreSceneSuccess,
    restoreSceneFailed,
} from './slice';
import { retrieveSurfaceDataForProduct } from 'src/services/surfaceClient';

type FetchSceneDataPayload = {
    sceneId: string;
};

type FetchSurfacesParams = {
    sku: string;
    skuVersion?: string | number;
    variables?: Record<string, unknown>;
    merchantSku?: string;
    merchantVersion?: string | number;
    merchantVariables?: Record<string, unknown>;
};

type Surface = {
    index: number;
    id: string;
    name: string;
    fullBleedArea: unknown;
    trimArea: unknown;
    foldLines: unknown;
    fullBleedAreas: unknown[];
    trimAreas: unknown[];
};

export function* fetchSceneData({ payload }: PayloadAction<FetchSceneDataPayload>): Generator<any, void, any> {
    const { sceneId } = payload;
    try {
        const scene = yield* call(getScene, { sceneId });
        yield* put(addScene(scene));
    } catch (e: any) {
        logError(`Failed to fetch scene from client: ${e}`);
    }
}

function* deleteSceneOnServer({ payload }: PayloadAction<string>): Generator<any, void, any> {
    try {
        yield* call(deleteAsset, { assetId: payload });
        yield* put(deleteSceneSuccess(payload));
    } catch (e: any) {
        yield* put(deleteSceneFailed(payload, apiErrorMessage(e)));
        logError(`Failed to delete scene to client: ${e}`);
    }
}

function* restoreSceneOnServer({ payload }: PayloadAction<string>): Generator<any, void, any> {
    try {
        yield* call(restoreAsset, { assetId: payload });
        yield* put(restoreSceneSuccess(payload));
    } catch (e: any) {
        yield* put(restoreSceneFailed(payload, apiErrorMessage(e)));
        logError(`Failed to restore scene to client: ${e}`);
    }
}

export function* fetchSurfaces({
    sku,
    skuVersion,
    variables,
    merchantSku,
    merchantVersion,
    merchantVariables,
}: FetchSurfacesParams): Generator<any, Surface[], any> {
    try {
        const surfaceData = (yield* call(retrieveSurfaceDataForProduct as any, {
            sku,
            skuVersion: typeof skuVersion === 'string' ? parseInt(skuVersion, 10) : skuVersion,
            variables,
            merchantSku,
            merchantVersion: typeof merchantVersion === 'string' ? parseInt(merchantVersion, 10) : merchantVersion,
            merchantVariables,
        })) as SurfaceData;
        // Assume one surface group for now
        const surfaces = surfaceData[0].surfaces.map((surface, i: number) => ({
            index: i + 1,
            id: surface.id,
            name: surface.name,
            fullBleedArea: surface.fullBleedArea,
            trimArea: surface.trimArea,
            foldLines: surface.foldLines,
            fullBleedAreas: ([] as any[]).concat(surface.fullBleedAreas || surface.fullBleedArea),
            trimAreas: ([] as any[]).concat(surface.trimAreas || surface.trimArea),
        }));

        return surfaces;
    } catch (error: any) {
        logError(`failed to retrieve surfaces: ${sku} ${JSON.stringify(variables)} ${error}`);
        throw error;
    }
}

export default function* scenesSaga() {
    return yield* all([
        yield* takeEvery(deleteScene, deleteSceneOnServer),
        yield* takeEvery(restoreScene, restoreSceneOnServer),
    ]);
}
